'use client';

import { Check, Globe, Play } from 'lucide-react';
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';

import styles from './styles.module.css';

export type LocaleSwitcherProps = {
  locales: readonly { value: string; label: string }[];
  currentLocale: string;
  onLocaleChange: (locale: string) => void;
  label: string;
  /** Align with a trailing header control when present. */
  menuOffset?: number;
};

/** The booking language selector, independent of routing and translation libraries. */
export function LocaleSwitcher({
  locales,
  currentLocale,
  onLocaleChange,
  label,
  menuOffset = 0,
}: LocaleSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) return;
    function closeOnOutsideClick(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node))
        setIsOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('click', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('click', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={styles.root}
      style={{ '--locale-menu-offset': `${menuOffset}px` } as CSSProperties}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setIsOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={() => setIsOpen((current) => !current)}
      >
        <Globe className={styles.icon} size={24} aria-hidden="true" />
      </button>
      {isOpen && (
        <>
          <Play
            className={`${styles.icon} ${styles.arrow}`}
            size={24}
            aria-hidden="true"
          />
          <div id={menuId} className={styles.menu}>
            <ul className={styles.list}>
              {locales.map((locale) => (
                <li key={locale.value}>
                  <button
                    type="button"
                    className={styles.option}
                    aria-pressed={locale.value === currentLocale}
                    onClick={() => {
                      setIsOpen(false);
                      triggerRef.current?.focus();
                      if (locale.value !== currentLocale)
                        onLocaleChange(locale.value);
                    }}
                  >
                    <span>{locale.label}</span>
                    {locale.value === currentLocale && (
                      <Check
                        className={`${styles.icon} ${styles.check}`}
                        size={24}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
